import { PresetChartType } from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/const';
import { LabelPosition } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v2/subchart/common/data-label.interface';
import { EMarkerSymbol, Position } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v2/chart-model.interface';
import { ILegendPos, STChartType, STDLblPos, STMarkerStyleDmlC } from '@tencent/chart-drawer-components/lib/interface';
import { STLegendPos, STDLblPos as SlideSTDLblPos, STMarkerStyleDmlC as SlideSTMarkerStyleDmlC } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';

/**
 * STChartType到CreateChartType的映射
 * STChartType: @tencent/chart-drawer-components里约定细分的总图表类型
 * PresetChartType: slide细分的总图表类型
 */
export const chartTypeMapping: Partial<Record<STChartType, PresetChartType>> = {
    [STChartType.ClusteredColumn]: PresetChartType.COLUMN_CLUSTERED,
    [STChartType.StackedColumn]: PresetChartType.COLUMN_STACKED,
    [STChartType.PercentStackedColumn]: PresetChartType.COLUMN_PERCENT_STACKED,

    [STChartType.Line]: PresetChartType.LINE_STANDARD,
    [STChartType.StackedLine]: PresetChartType.LINE_STACKED,
    [STChartType.PercentStackedLine]: PresetChartType.LINE_PERCENT_STACKED,
    [STChartType.MarkerLine]: PresetChartType.LINE_STANDARD_WITH_POINT,
    [STChartType.StackedMarkerLine]: PresetChartType.LINE_STACKED_WITH_POINT,
    [STChartType.PercentStackedMarkerLine]: PresetChartType.LINE_PERCENT_STACKED_WITH_POINT,

    [STChartType.ClusteredBar]: PresetChartType.BAR_CLUSTERED,
    [STChartType.StackedBar]: PresetChartType.BAR_STACKED,
    [STChartType.PercentStackedBar]: PresetChartType.BAR_PERCENT_STACKED,

    [STChartType.Pie]: PresetChartType.PIE,
    [STChartType.PieOfPie]: PresetChartType.OF_PIE,
    [STChartType.BarOfPie]: PresetChartType.OF_PIE_BAR,
    [STChartType.Doughnut]: PresetChartType.DOUGHNUT,

    [STChartType.Area]: PresetChartType.AREA,
    [STChartType.StackedArea]: PresetChartType.AREA_STACKED,
    [STChartType.PercentStackedArea]: PresetChartType.AREA_PERCENT_STACKED,

    [STChartType.Radar]: PresetChartType.RADAR,
    [STChartType.MarkerRadar]: PresetChartType.RADAR_WITH_POINT,
    [STChartType.FilledRadar]: PresetChartType.RADAR_WITH_FILL,

    [STChartType.Scatter]: PresetChartType.SCATTER,
    [STChartType.Bubble]: PresetChartType.BUBBLE,

    [STChartType.HighLowCloseStock]: PresetChartType.STOCK,

    [STChartType.ClusteredColumnAndLineCombo]: PresetChartType.COMBO,
};

export const legendPositionMapping: Record<ILegendPos, STLegendPos> = {
    [ILegendPos.BOTTOM]: STLegendPos.b,
    [ILegendPos.LEFT]: STLegendPos.l,
    [ILegendPos.RIGHT]: STLegendPos.r,
    [ILegendPos.TOP]: STLegendPos.t,
    [ILegendPos.TOP_RIGHT]: STLegendPos.tr,
};

export const dataLabelPositionMapping: Partial<Record<STDLblPos, SlideSTDLblPos>> = {
    [STDLblPos.B]: SlideSTDLblPos.b,
    [STDLblPos.BestFit]: SlideSTDLblPos.bestFit,
    [STDLblPos.Ctr]: SlideSTDLblPos.ctr,
    [STDLblPos.InBase]: SlideSTDLblPos.inBase,
    [STDLblPos.InEnd]: SlideSTDLblPos.inEnd,
    [STDLblPos.L]: SlideSTDLblPos.l,
    [STDLblPos.OutEnd]: SlideSTDLblPos.outEnd,
    [STDLblPos.R]: SlideSTDLblPos.r,
    [STDLblPos.T]: SlideSTDLblPos.t,
};

export const markerSymbolMapping: Partial<Record<STMarkerStyleDmlC, SlideSTMarkerStyleDmlC>> = {
    [STMarkerStyleDmlC.Circle]: SlideSTMarkerStyleDmlC.circle,
    [STMarkerStyleDmlC.Dash]: SlideSTMarkerStyleDmlC.dash,
    [STMarkerStyleDmlC.Diamond]: SlideSTMarkerStyleDmlC.diamond,
    [STMarkerStyleDmlC.Dot]: SlideSTMarkerStyleDmlC.dot,
    [STMarkerStyleDmlC.None]: SlideSTMarkerStyleDmlC.none,
    [STMarkerStyleDmlC.Picture]: SlideSTMarkerStyleDmlC.picture,
    [STMarkerStyleDmlC.Plus]: SlideSTMarkerStyleDmlC.plus,
    [STMarkerStyleDmlC.Square]: SlideSTMarkerStyleDmlC.square,
    [STMarkerStyleDmlC.Star]: SlideSTMarkerStyleDmlC.star,
    [STMarkerStyleDmlC.Triangle]: SlideSTMarkerStyleDmlC.triangle,
    [STMarkerStyleDmlC.X]: SlideSTMarkerStyleDmlC.x,
};

export function mappingToSTType<T>(mapping: T, type: T[keyof T], defaultSTType: keyof T): keyof T {
    const STType = Object.keys(mapping as object).find(key => mapping[key] === type) as keyof T;
    return isNaN(STType as number) ? STType : Number(STType) as keyof T ?? defaultSTType;
}

/**
 * @param slideType chart model图表类型
 * @returns @tencent/chart-drawer-components里约定的图表类型
 */
export function mappingSlideChartTypeToSTChartType(slideType: PresetChartType): STChartType {
    return  mappingToSTType<typeof chartTypeMapping>(chartTypeMapping, slideType, STChartType.ClusteredColumn);
}

/**
 * @param position chart model的图例位置类型
 * @returns @tencent/chart-drawer-components里约定的图例位置类型
 */
export function mappingPositionToILegendPos(position: STLegendPos): ILegendPos {
    return mappingToSTType<typeof legendPositionMapping>(legendPositionMapping, position, ILegendPos.BOTTOM);
}

/**
 * @param position chart model的数据标签位置类型
 * @returns @tencent/chart-drawer-components里约定的数据标签位置类型
 */
export function mappingPositionToSTDLblPos(position: SlideSTDLblPos): STDLblPos {
    return mappingToSTType<typeof dataLabelPositionMapping>(
        dataLabelPositionMapping,
        position, STDLblPos.PBDEFAULT,
    );
}

/**
 * @param position chart model的标记类型
 * @returns @tencent/chart-drawer-components里约定的标记类型
 */
export function mappingSymbolToSTMarkerStyleDmlC(symbol: SlideSTMarkerStyleDmlC): STMarkerStyleDmlC {
    return mappingToSTType<typeof markerSymbolMapping>(markerSymbolMapping, symbol, STMarkerStyleDmlC.Circle);
}


